import { useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid2';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import PlaylistAddOutlinedIcon from '@mui/icons-material/PlaylistAddOutlined';
import SearchIcon from '@mui/icons-material/Search';
import { ApiError } from '../api/client';
import { exportsApi, invoicesApi } from '../api/endpoints';
import { PageHeader } from '../components/PageHeader';
import { StatusBadge } from '../components/StatusBadge';
import { EmptyState } from '../components/EmptyState';
import { ExportMenu } from '../components/ExportMenu';
import { TableSkeleton } from '../components/TableSkeleton';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { useToast } from '../app/ToastProvider';
import { clientLabel } from '../utils/client';
import { formatDate, formatPeso } from '../utils/format';

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'ISSUED', label: 'Issued' },
  { value: 'PARTIALLY_PAID', label: 'Partially Paid' },
  { value: 'PAID', label: 'Paid' },
  { value: 'VOID', label: 'Void' },
];

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const COLUMN_COUNT = 9;

function GenerateInvoicesDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);

  const toast = useToast();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: () => invoicesApi.generateBatch({ billingYear: year, billingMonth: month }),
    onSuccess: async ({ data, message }) => {
      toast.success(message);
      if (data.skipped.length > 0) {
        toast.notify(
          `Skipped ${data.skipped.length}: ${data.skipped
            .slice(0, 3)
            .map((entry) => entry.subscriptionNumber)
            .join(', ')}${data.skipped.length > 3 ? '…' : ''}`,
          'warning',
        );
      }
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['invoices'] }),
        queryClient.invalidateQueries({ queryKey: ['clients'] }),
        queryClient.invalidateQueries({ queryKey: ['dashboard'] }),
      ]);
      onClose();
    },
    onError: (caught: unknown) => {
      toast.error(
        caught instanceof ApiError ? caught.message : 'The invoices could not be generated.',
      );
    },
  });

  return (
    <Dialog open={open} onClose={mutation.isPending ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Generate Monthly Invoices</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Issues one invoice per <strong>active</strong> subscription for the selected billing
          month, using each subscription&apos;s billing day and its plan&apos;s current price.
          Subscriptions already invoiced for that period are skipped.
        </DialogContentText>
        <Grid container spacing={2}>
          <Grid size={7}>
            <TextField
              select
              label="Billing Month"
              value={month}
              onChange={(event) => setMonth(Number(event.target.value))}
            >
              {MONTHS.map((label, index) => (
                <MenuItem key={label} value={index + 1}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={5}>
            <TextField
              label="Year"
              type="number"
              value={year}
              onChange={(event) => setYear(Number(event.target.value))}
              slotProps={{ htmlInput: { min: 2000, max: 2999 } }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={mutation.isPending}>
          Cancel
        </Button>
        <Button variant="contained" onClick={() => mutation.mutate()} disabled={mutation.isPending}>
          {mutation.isPending ? 'Generating…' : 'Generate'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function InvoicesPage() {
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [overdueOnly, setOverdueOnly] = useState(searchParams.get('overdueOnly') === 'true');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState<number | null>(null);
  const toast = useToast();

  const debouncedSearch = useDebouncedValue(search);

  const filters = {
    search: debouncedSearch || undefined,
    status: status || undefined,
    overdueOnly: overdueOnly ? 'true' : undefined,
  };

  const queryParams = { page: page + 1, pageSize, ...filters };

  const { data, isPending, isError, error } = useQuery({
    queryKey: ['invoices', queryParams],
    queryFn: () => invoicesApi.list(queryParams),
    placeholderData: keepPreviousData,
  });

  const downloadInvoice = async (invoiceId: number, invoiceNumber: string): Promise<void> => {
    setDownloadingInvoiceId(invoiceId);
    try {
      await invoicesApi.download(invoiceId, invoiceNumber);
    } catch (caught) {
      toast.error(caught instanceof ApiError ? caught.message : 'The invoice PDF could not be downloaded.');
    } finally {
      setDownloadingInvoiceId(null);
    }
  };

  return (
    <>
      <PageHeader
        title="Invoices"
        description="Monthly billing obligations issued against active subscriptions."
        actions={
          <>
            <ExportMenu
              onExport={(format) => exportsApi.invoices(format, filters)}
              disabled={(data?.items.length ?? 0) === 0}
            />
            <Button
              variant="contained"
              startIcon={<PlaylistAddOutlinedIcon />}
              onClick={() => setGenerateOpen(true)}
            >
              Generate Invoices
            </Button>
          </>
        }
      />

      {isError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error instanceof Error ? error.message : 'Invoices could not be loaded.'}
        </Alert>
      ) : null}

      <Card>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          sx={{ p: 2 }}
          alignItems={{ md: 'center' }}
        >
          <TextField
            placeholder="Search invoice or client…"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(0);
            }}
            sx={{ maxWidth: { md: 320 } }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
          <TextField
            select
            label="Status"
            value={status}
            onChange={(event) => {
              setStatus(event.target.value);
              setPage(0);
            }}
            sx={{ maxWidth: { md: 200 } }}
            disabled={overdueOnly}
          >
            {STATUS_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <FormControlLabel
            control={
              <Switch
                checked={overdueOnly}
                onChange={(event) => {
                  setOverdueOnly(event.target.checked);
                  setPage(0);
                }}
              />
            }
            label="Overdue only"
          />
        </Stack>

        <TableContainer>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Invoice Number</TableCell>
                <TableCell>Client</TableCell>
                <TableCell>Billing Period</TableCell>
                <TableCell>Due Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell align="right">Paid</TableCell>
                <TableCell align="right">Outstanding</TableCell>
                <TableCell align="center">Download</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isPending ? (
                <TableSkeleton columns={COLUMN_COUNT} />
              ) : data && data.items.length > 0 ? (
                data.items.map((invoice) => (
                  <TableRow key={invoice.id} hover>
                    <TableCell>{invoice.invoiceNumber}</TableCell>
                    <TableCell>
                      <Link
                        component={RouterLink}
                        to={`/clients/${invoice.subscription.client.id}`}
                      >
                        {clientLabel(invoice.subscription.client)}
                      </Link>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {invoice.subscription.servicePlan.planName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {formatDate(invoice.billingPeriodStart)} –{' '}
                      {formatDate(invoice.billingPeriodEnd)}
                    </TableCell>
                    <TableCell>{formatDate(invoice.dueDate)}</TableCell>
                    <TableCell>
                      <StatusBadge status={invoice.status} />
                    </TableCell>
                    <TableCell align="right">{formatPeso(invoice.totalAmount)}</TableCell>
                    <TableCell align="right">{formatPeso(invoice.amountPaid)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {formatPeso(invoice.outstandingAmount)}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={`Download ${invoice.invoiceNumber} PDF`}>
                        <span>
                          <IconButton
                            size="small"
                            color="primary"
                            aria-label={`Download invoice ${invoice.invoiceNumber} as PDF`}
                            disabled={downloadingInvoiceId === invoice.id}
                            onClick={() => void downloadInvoice(invoice.id, invoice.invoiceNumber)}
                          >
                            <DownloadOutlinedIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT} sx={{ border: 0 }}>
                    <EmptyState
                      message="No invoices found."
                      hint="Generate monthly invoices, or adjust the filters."
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={data?.meta.total ?? 0}
          page={page}
          onPageChange={(_event, nextPage) => setPage(nextPage)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(event) => {
            setPageSize(Number(event.target.value));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Card>

      <GenerateInvoicesDialog open={generateOpen} onClose={() => setGenerateOpen(false)} />
    </>
  );
}
